import { CAN_APPROVE_EDD, type StaffPermission } from '../../auth/permissions.js';

export { CAN_APPROVE_EDD };

/**
 * The "EDD approval" switch on the Admin Users page (DEV-112).
 *
 * Mirrors what the server enforces on PATCH /admin/auth/admins/:id/permissions
 * so the switch says why before anybody clicks: nobody grants a permission to
 * themselves (giving it up is fine), and only staff whose role decides KYC can
 * hold it. The server checks all of it again.
 */

export interface EddPermissionControl {
  readonly checked: boolean;
  readonly disabled: boolean;
  /** Why it is disabled; null when it is not. */
  readonly hint: string | null;
}

const DECIDING_ROLES: readonly string[] = ['admin', 'compliance'];

/** A permission list with one permission set on or off; the input is left alone. */
export function withPermission(
  current: readonly string[],
  permission: StaffPermission,
  on: boolean,
): string[] {
  const others = current.filter((p) => p !== permission);
  return on ? [...others, permission] : others;
}

export function eddPermissionControl(
  me: { readonly id: string } | null | undefined,
  staff: { readonly id: string; readonly roles: readonly string[]; readonly permissions?: readonly string[] },
): EddPermissionControl {
  const checked = staff.permissions?.includes(CAN_APPROVE_EDD) === true;
  if (checked) return { checked, disabled: false, hint: null };
  if (me?.id === staff.id) {
    return {
      checked,
      disabled: true,
      hint: 'You cannot grant yourself this permission. Another administrator has to grant it.',
    };
  }
  if (!staff.roles.some((role) => DECIDING_ROLES.includes(role.toLowerCase()))) {
    return {
      checked,
      disabled: true,
      hint: 'Only compliance and admin staff can decide KYC, so only they can hold this permission.',
    };
  }
  return { checked, disabled: false, hint: null };
}
